import SaveIcon from "@mui/icons-material/Save";
import { Box, CircularProgress, Divider, Fab, Grid, Typography } from "@mui/material";
import { type CalendarDate, fromCalendarDate } from "@repo/utils/date";
import { revalidateLogic } from "@tanstack/react-form";
import { useSelector } from "@tanstack/react-store";
import { useRef } from "react";
import { toast } from "react-toastify";
import { useAppForm } from "@/components/Forms";
import { useUpdateDriverMutation } from "@/libs/generated/hooks";
import type { GetDriverByIdQuery } from "@/libs/generated/operations";
import { queryClient } from "@/libs/queryClient";
import { DriverFormSchema, GetDriverFormDefaultValues } from "@/schemas/driver";

import { DriverDocumentsSection } from "./DriverDocumentsSection";
import { DriverInfoFields } from "./DriverInfoFields";

type DriverType = GetDriverByIdQuery["getDriverById"];

/** A calendar date is sent as its UTC-midnight instant. */
const toCalendarDateInput = (date: CalendarDate | undefined) =>
  date ? fromCalendarDate(date).toISOString() : undefined;

const __inferFormType = () =>
  useAppForm({
    defaultValues: GetDriverFormDefaultValues(),
    validationLogic: revalidateLogic(),
    validators: { onChange: DriverFormSchema },
    onSubmit: async () => {},
  });
export type DriverFormType = ReturnType<typeof __inferFormType>;

type DriverFormSimpleProps = {
  data: DriverType;
  children?: React.ReactNode;
};

function FloatingSaveButton({
  formRef,
  loading,
}: {
  formRef: React.RefObject<HTMLFormElement | null>;
  loading: boolean;
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        right: "3vw",
        bottom: "3vh",
        zIndex: 1000,
      }}
    >
      <Fab
        variant="extended"
        color="primary"
        disabled={loading}
        onClick={() => formRef.current?.requestSubmit()}
      >
        {loading ? <CircularProgress size={20} /> : <SaveIcon sx={{ mr: 1 }} />}
        Sauvegarder
      </Fab>
    </Box>
  );
}

export default function DriverFormSimple({ data: driverData, children }: DriverFormSimpleProps) {
  const formRef = useRef<HTMLFormElement>(null);

  const updateDriverMutation = useUpdateDriverMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getDriverById"] });
    },
  });
  const loading = updateDriverMutation.isPending;

  const form = useAppForm({
    defaultValues: GetDriverFormDefaultValues(driverData),
    validationLogic: revalidateLogic(),
    validators: { onChange: DriverFormSchema },
    onSubmit: async ({ value }) => {
      if (!driverData?.id) return;

      const { docs: _docs, ...input } = value;
      const updateInput = {
        ...input,
        id: driverData.id,
        birthDate: toCalendarDateInput(input.birthDate),
        drivingLicenseDate: toCalendarDateInput(input.drivingLicenseDate),
        drivingPermitDueDate: toCalendarDateInput(input.drivingPermitDueDate),
        insurance_expiration_date: toCalendarDateInput(input.insurance_expiration_date),
        vigilance_issue_date: toCalendarDateInput(input.vigilance_issue_date),
        kbis_issue_date: toCalendarDateInput(input.kbis_issue_date),
      };
      try {
        await updateDriverMutation.mutateAsync({
          input: updateInput,
        });
        form.reset(value);
        toast.success("Les informations ont été enregistrées");
      } catch (_error) {
        toast.error("Erreur lors de l'enregistrement");
      }
    },
  });

  const hasChanges = useSelector(form.store, (s) => {
    const { docs: _currentDocs, ...currentValues } = s.values;
    const defaultVals = GetDriverFormDefaultValues(driverData);
    const { docs: _defaultDocs, ...defaultValues } = defaultVals;
    return JSON.stringify(currentValues) !== JSON.stringify(defaultValues);
  });
  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Box
          sx={{
            backgroundColor: "#E7E6F7",
            borderRadius: 1,
            padding: "20px",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: "#00244f" }}>
            Informations chauffeur
          </Typography>
          <Divider sx={{ mb: 3, borderColor: "#00244f", opacity: 0.2 }} />

          <Grid container spacing={2}>
            <DriverInfoFields form={form} />
            <DriverDocumentsSection form={form} driverData={driverData} />
            {children !== null && children !== undefined && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                {children}
              </Grid>
            )}
          </Grid>
        </Box>

        {hasChanges && <FloatingSaveButton formRef={formRef} loading={loading} />}
      </form.AppForm>
    </form>
  );
}
